import { GetServerSidePropsContext, NextPage } from 'next'
import qs from 'query-string'

import { search } from '../../algolia'
import {
  setDaysFilter,
  setFilter,
  setStringFilter,
} from '../../algolia/helpers'
import { initWithLangAndLabels } from '../../i18n'
import { useSearchPageProps } from '../../relay/Classified/useSearchProps'
import {
  GetServerSidePropsReturnType,
  PageProps,
} from '../../relay/Common/PageProps'
import RestApiClient from '../../rest/client'
import getCategories from '../../rest/requests/classifieds/getCategories'
import getSpecialties from '../../rest/requests/classifieds/getSpecialties'
import getConfiguration from '../../rest/services/getConfiguration'
import {
  Classified,
  ClassifiedCategories,
  ClassifiedSpecialties,
  Status,
} from '../../rest/types/Classified'
import SearchTemplate from '../../templates/SearchTemplate'
import { Index } from '../../types/algolia'
import { ListDisplayType } from '../../types/classified'

type Props = PageProps & {
  classifieds: Array<Classified>
  numberClassifieds: number
  specialties: ClassifiedSpecialties
  categories: ClassifiedCategories
  filters: {
    specialities: Array<string> | string | null
    offerType: Array<string> | string | null
    localization: string | null
    terms: string | null
    radius: string
    lat: string
    lng: string
    startDate: string
    endDate: string
    days: string[]
  }
  currentPage: number
  nbPages: number
  displayType: ListDisplayType
  isError?: boolean
}

const ClassifiedsEmitter: NextPage<Props> = ({
  configuration,
  classifieds,
  numberClassifieds,
  categories,
  specialties,
  filters,
  currentPage,
  nbPages,
  displayType,
  isError,
}) => {
  const props = useSearchPageProps({
    categories,
    configuration,
    specialties: specialties,
    filters,
    classifieds,
    numberClassifieds,
    pagination: {
      nbPages,
      currentPage,
    },
    displayTypeParams: displayType,
    isError,
  })

  return <SearchTemplate {...props} />
}

export default ClassifiedsEmitter

export async function getServerSideProps(
  context: GetServerSidePropsContext
): GetServerSidePropsReturnType<Props> {
  const query = context.resolvedUrl.split('?')?.[1] ?? ''
  const queryParams = qs.parse(query, { arrayFormat: 'comma' })

  const [configuration, specialtiesResponse, categoriesResponse] =
    await Promise.all([
      getConfiguration(),
      await RestApiClient.call(
        getSpecialties({
          pagination: {
            pageSize: 100,
          },
          sort: ['name'],
        })
      ),
      await RestApiClient.call(getCategories()),
    ])

  const offerType: any = queryParams?.offerType ?? null
  const localization: any = context?.query?.localization ?? ''
  const radius: any = context?.query?.radius ?? ''
  const lat: any = context?.query?.lat ?? ''
  const lng: any = context?.query?.lng ?? ''
  const specialities: any = queryParams?.specialities ?? null
  const terms: any = queryParams?.terms ?? ''
  const page: any = queryParams?.page ? Number(queryParams.page) - 1 : 0
  const displayType: any = queryParams?.displayType ?? null
  const defaultSpeciality: any =
    configuration?.config?.default_specialty?.documentId
  const startDate: any = queryParams?.startDate ?? null
  const endDate: any = queryParams?.endDate ?? null
  const days: any = queryParams?.days ?? null

  const allSpecialties = specialities
    ? Array.isArray(specialities)
      ? [...specialities, ...(defaultSpeciality ? [defaultSpeciality] : [])]
      : [specialities, ...(defaultSpeciality ? [defaultSpeciality] : [])]
    : null

  const allOfferType = offerType
    ? Array.isArray(offerType)
      ? [...offerType]
      : [offerType]
    : null

  const facetFilters =
    setFilter('classifiedStatus', Status.Published) +
    (allSpecialties
      ? ' AND ' +
        setStringFilter(
          'block.specialities.documentId',
          allSpecialties as any,
          'OR'
        ) +
        ' OR ' +
        setStringFilter(
          'block.speciality.documentId',
          allSpecialties as any,
          'OR'
        )
      : '') +
    (allOfferType
      ? ' AND ' +
        setStringFilter('classifiedCategory.documentId', allOfferType, 'OR')
      : '') +
    (days ? ` AND ${setDaysFilter('block.period', days, 'OR')}` : '')

  const geoSearchOptions =
    lat && lng
      ? {
          aroundLatLng: `${lat},${lng}`,
          aroundRadius: radius ? parseInt(radius, 10) : undefined,
        }
      : {}

  const numericFilters =
    startDate && endDate
      ? [
          // Overlap condition: itemStart <= filterEnd AND itemEnd >= filterStart
          `unixStart <= ${new Date(endDate).getTime()}`,
          `unixEnd >= ${new Date(startDate).getTime()}`,
        ]
      : []

  const results = await search(Index.Classified, terms, {
    filters: facetFilters,
    page,
    numericFilters,
    ...geoSearchOptions,
    ...(displayType === ListDisplayType.Map && {
      hitsPerPage: 1000,
    }),
  })

  const classifieds = (results?.hits as unknown as Array<Classified>) ?? []
  const numberClassifieds = results?.nbHits || 0
  const nbPages = results?.nbPages || 0
  const currentPage = (results?.page ?? 0) + 1
  const isError = results?.error ?? false

  const specialties = specialtiesResponse.data ?? []
  const categories = categoriesResponse.data ?? []
  const i18n = initWithLangAndLabels('fr', configuration.translations)

  const filters = {
    offerType,
    specialities,
    terms,
    localization,
    radius,
    lat,
    lng,
    startDate,
    endDate,
    days,
  }

  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  )

  return {
    props: {
      isError,
      configuration,
      classifieds,
      numberClassifieds,
      specialties,
      categories,
      filters,
      currentPage,
      nbPages,
      i18n,
      displayType,
    },
  }
}
